import { IS_DEMO, asset } from "./asset";

export class ApiError extends Error {
  code: string;
  status: number;
  data: any;
  constructor(code: string, status: number, data?: any) {
    super(code);
    this.code = code;
    this.status = status;
    this.data = data;
  }
}

export interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Raw body (e.g. audio Blob) — sent as-is with its own content type. */
  raw?: Blob | ArrayBuffer;
  timeoutMs?: number;
}

/** Thin JSON client for /api. Every state-changing request carries the CSRF header. */
export async function api<T = any>(path: string, opts: ApiOptions = {}): Promise<T> {
  if (IS_DEMO) throw new ApiError("demo", 0);
  const method = opts.method ?? (opts.body !== undefined || opts.raw ? "POST" : "GET");
  const headers: Record<string, string> = { "X-HG": "1", ...(opts.headers ?? {}) };
  let body: BodyInit | undefined;
  if (opts.raw) {
    body = opts.raw as BodyInit;
    if (opts.raw instanceof Blob && opts.raw.type && !headers["Content-Type"]) headers["Content-Type"] = opts.raw.type;
  } else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  const ctrl = new AbortController();
  const timer = opts.timeoutMs ? setTimeout(() => ctrl.abort(), opts.timeoutMs) : null;
  if (opts.signal) opts.signal.addEventListener("abort", () => ctrl.abort(), { once: true });
  let res: Response;
  try {
    res = await fetch(asset("api" + path), { method, headers, body, credentials: "same-origin", signal: ctrl.signal });
  } catch (e) {
    throw new ApiError(ctrl.signal.aborted ? "timeout" : "network", 0);
  } finally {
    if (timer) clearTimeout(timer);
  }
  let data: any = null;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) {
    try {
      data = await res.json();
    } catch {
      data = null;
    }
  }
  if (!res.ok || (data && data.ok === false)) {
    throw new ApiError((data && data.error) || (res.status === 429 ? "rate_limited" : "server"), res.status, data);
  }
  return data as T;
}

export const errorCode = (e: unknown): string => (e instanceof ApiError ? e.code : "generic");
